/**
 * Ring buffer of console output for the main browser thread.
 *
 * This is installed by the bug reporter bundle, which loads before the game
 * bundle, so a crash while the game is still starting up is still captured.
 */

/** Roughly a few hundred KB of text at typical line lengths. */
const MaxEntries = 4000;
/** One absurd log line (a whole game state dump, say) shouldn't eat the buffer. */
const MaxEntryLength = 4000;
/** How far back the buffer is cut when it fills. See push(). */
const TrimToEntries = 3000;

type LogEntry = {
	time: number;
	level: string;
	text: string;
};

const entries: LogEntry[] = [];
let installed = false;

function push(level: string, text: string) {
	if (text.length > MaxEntryLength) {
		text = text.slice(0, MaxEntryLength) + `… [truncated, ${text.length} chars]`;
	}
	entries.push({ time: Date.now(), level, text });
	// Trim in batches, not on every push. Splicing one entry off the front of a
	// full buffer moves every remaining element, which made a single console.log
	// cost ~19us in steady state; dropping a block at a time amortises that to
	// ~0.1us. The buffer just floats between TrimToEntries and MaxEntries.
	if (entries.length > MaxEntries) {
		entries.splice(0, entries.length - TrimToEntries);
	}
}

/** Best-effort stringify - never throws, never recurses into the console. */
function format(value: any): string {
	if (typeof value === 'string') return value;
	if (value instanceof Error) return `${value.name}: ${value.message}\n${value.stack ?? ''}`;
	if (value === undefined) return 'undefined';
	if (value === null) return 'null';
	try {
		const seen = new WeakSet();
		const json = JSON.stringify(value, (_key, val) => {
			if (typeof val === 'object' && val !== null) {
				if (seen.has(val)) return '[Circular]';
				seen.add(val);
			}
			if (typeof val === 'function') return '[Function]';
			if (typeof val === 'bigint') return val.toString();
			return val;
		});
		return json ?? String(value);
	} catch {
		try { return String(value); } catch { return '[unprintable]'; }
	}
}

export function installConsoleCapture() {
	if (installed) return;
	installed = true;

	const levels: Array<'log' | 'info' | 'warn' | 'error' | 'debug'> = ['log', 'info', 'warn', 'error', 'debug'];
	for (const level of levels) {
		const original = console[level]?.bind(console);
		console[level] = (...args: any[]) => {
			try {
				push(level, args.map(format).join(' '));
			} catch {
				// Capture must never break the game's own logging.
			}
			original?.(...args);
		};
	}

	window.addEventListener('error', (event: ErrorEvent) => {
		const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : '';
		push('uncaught', `${event.message}${where}\n${event.error?.stack ?? ''}`);
	});

	window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
		push('unhandledrejection', format(event.reason));
	});

	push('bugreport', `console capture installed at ${new Date().toISOString()}`);
}

/** Records something in the log without also printing it to the real console. */
export function recordInternal(text: string) {
	push('bugreport', text);
}

export function getCapturedLogText(): string {
	return entries
		.map(e => `[${new Date(e.time).toISOString()}] [${e.level}] ${e.text}`)
		.join('\n');
}
